import { useState, useMemo } from "react";
import useSWR from "swr";
import { Panel, PanelHeader } from "@/components/common/Card";
import { getLogs, timeAgo } from "@/lib/api";
import { LOGS } from "@/constants/testIds";
import {
  Funnel,
  Terminal,
  Info,
  CheckCircle,
  Warning,
  XCircle,
} from "@phosphor-icons/react";

// UI label -> backend value (backend stores levels as info|success|warn|error)
const LEVELS = [
  { v: "", l: "All Levels" },
  { v: "info", l: "Info" },
  { v: "success", l: "Success" },
  { v: "warn", l: "Warning" },
  { v: "error", l: "Error" },
];

const CATEGORIES = [
  { v: "", l: "All Categories" },
  { v: "scan", l: "Scan" },
  { v: "strategy", l: "Strategy" },
  { v: "signal", l: "Signal" },
  { v: "telegram", l: "Telegram" },
  { v: "api", l: "API" },
  { v: "system", l: "System" },
];

const LEVEL_STYLE = {
  info: { color: "text-[#2563eb]", Icon: Info },
  success: { color: "text-[#16a34a]", Icon: CheckCircle },
  warn: { color: "text-[#d97706]", Icon: Warning },
  error: { color: "text-[#dc2626]", Icon: XCircle },
};

const SelectInput = ({ value, onChange, options, testid }) => (
  <select
    data-testid={testid}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="bg-[#ffffff] border border-slate-200 hover:border-slate-300 text-slate-900 text-xs font-data rounded-sm px-3 py-2 focus:border-[#2563eb] focus:outline-none"
  >
    {options.map((o) => (
      <option key={o.v} value={o.v}>
        {o.l}
      </option>
    ))}
  </select>
);

const LogRow = ({ log }) => {
  const meta = LEVEL_STYLE[log.level] || LEVEL_STYLE.info;
  const Icon = meta.Icon;
  return (
    <div
      data-testid={`log-row-${log.id}`}
      className="grid grid-cols-[110px_90px_90px_1fr] gap-3 px-4 py-2 border-b border-slate-200/70 hover:bg-[#101015] text-xs font-data"
    >
      <span className="text-slate-500 truncate">
        {new Date(log.created_at).toLocaleTimeString(undefined, {
          hour12: false,
        })}
      </span>
      <span className={`inline-flex items-center gap-1.5 ${meta.color} uppercase tracking-wider font-bold`}>
        <Icon size={11} weight="fill" />
        {log.level}
      </span>
      <span className="text-slate-600 uppercase tracking-wider">
        {log.category}
      </span>
      <span className="text-slate-800 break-words whitespace-pre-wrap">
        {log.message}
        {log.meta && Object.keys(log.meta).length > 0 && (
          <span className="ml-2 text-slate-400 text-[11px]">
            · {Object.entries(log.meta).slice(0, 4).map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`).join(" ")}
          </span>
        )}
      </span>
    </div>
  );
};

export const Logs = () => {
  const [level, setLevel] = useState("");
  const [category, setCategory] = useState("");

  const params = useMemo(() => {
    const p = { limit: 500 };
    if (level) p.level = level;
    if (category) p.category = category;
    return p;
  }, [level, category]);

  const { data: logs, isLoading } = useSWR(
    ["/logs", params],
    () => getLogs(params),
    { refreshInterval: 10000 },
  );

  const rows = logs || [];

  return (
    <div className="flex flex-col gap-6 max-w-[1600px]">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-bold">
            System Stream
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-black tracking-tighter text-slate-900 mt-1">
            Logs
          </h1>
          <div className="text-xs text-slate-500 mt-2">
            Engine output across scanner, strategies, API, and Telegram delivery.
          </div>
        </div>
        <div className="text-xs text-slate-500 font-data">
          {rows.length} entries · auto-refresh 10s
        </div>
      </div>

      {/* Filters */}
      <Panel>
        <div className="flex flex-wrap items-center gap-3 p-4">
          <Funnel size={14} className="text-slate-500" />
          <SelectInput
            testid={LOGS.filterLevel}
            value={level}
            onChange={setLevel}
            options={LEVELS}
          />
          <SelectInput
            testid={LOGS.filterCategory}
            value={category}
            onChange={setCategory}
            options={CATEGORIES}
          />
          {(level || category) && (
            <button
              data-testid="logs-clear-filters"
              onClick={() => {
                setLevel("");
                setCategory("");
              }}
              className="text-[10px] tracking-widest uppercase text-slate-600 hover:text-slate-900 px-2"
            >
              Clear
            </button>
          )}
        </div>
      </Panel>

      {/* Console */}
      <Panel>
        <PanelHeader
          title="Live Log Stream"
          hint="Newest first · most recent 500 entries"
          action={<Terminal size={14} className="text-slate-500" />}
        />
        <div
          data-testid={LOGS.console}
          className="max-h-[70vh] overflow-y-auto"
        >
          {/* Header row */}
          <div className="grid grid-cols-[110px_90px_90px_1fr] gap-3 px-4 py-2 text-[10px] tracking-[0.1em] uppercase text-slate-500 border-b border-slate-200/70 bg-[#f1f5f9] sticky top-0">
            <span>Time</span>
            <span>Level</span>
            <span>Category</span>
            <span>Message</span>
          </div>
          {isLoading ? (
            <div className="px-4 py-10 text-center text-xs text-slate-400">
              Loading log stream…
            </div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-12 text-center text-xs text-slate-400">
              <Terminal size={28} className="mx-auto mb-2 text-slate-400" />
              No log entries match your filters.
            </div>
          ) : (
            rows.map((log) => <LogRow key={log.id} log={log} />)
          )}
        </div>
      </Panel>
    </div>
  );
};
